/**
 * DesignSystemPage.tsx — Rivmoon Console 设计系统活文档（页面入口）
 *
 * 移植自原型 design/Novel-admin/src/pages/design-system.jsx 的壳 + NAV + DSContent 分发器。
 * 原型用 window.__ds / window.__dsTokenSections / __dsComponentSections /
 * __dsPatternSections 跨文件注册；这里改为 ESM：各 section 组件直接 import 组装，
 * 去掉 window 注册表。Intro / Values / Cases 三个介绍组件就近内联于本文件。
 * window.Icons → import { Icons }；视觉层（className / inline-style）与原型 1:1 保真。
 */

import { useState } from 'react';
import type { ComponentType } from 'react';
import { Icons } from '~/lib/icons';
import {
  DSCButton,
  DSCCard,
  DSCChip,
  DSCDialog,
  DSCFormRow,
  DSCInput,
  DSCMultilang,
  DSCSelect,
  DSCStatus,
  DSCSwitch,
  DSCTable,
  DSCToast,
} from './components';
import { DSBlock, DSHeader } from './ds-primitives';
import {
  DSPDensity,
  DSPFallback,
  DSPI18n,
  DSPMono,
  DSPStatus,
  DSPatternButtons,
  DSPatternCopy,
  DSPatternDisplay,
  DSPatternFeedback,
  DSPatternFormat,
  DSPatternInput,
  DSPatternLists,
  DSPatternNav,
} from './patterns';
import { DSColors, DSIcons, DSLayout, DSShadow, DSTheme, DSType } from './tokens';

interface NavItem {
  id: string;
  label: string;
}
interface NavGroup {
  id: string;
  section: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    id: 'intro',
    section: 'Rivmoon Console DS',
    items: [
      { id: 'intro', label: '介绍' },
      { id: 'values', label: '设计价值观' },
      { id: 'cases', label: '实践案例' },
    ],
  },
  {
    id: 'tokens',
    section: '全局样式',
    items: [
      { id: 'colors', label: '色彩' },
      { id: 'layout', label: '布局与密度' },
      { id: 'type', label: '字体' },
      { id: 'icons', label: '图标' },
      { id: 'theme', label: '明暗模式' },
      { id: 'shadow', label: '阴影 · 圆角' },
    ],
  },
  {
    id: 'patterns',
    section: '设计模式 · 全局规则',
    items: [
      { id: 'feedback', label: '反馈' },
      { id: 'navigation', label: '导航' },
      { id: 'input', label: '数据录入' },
      { id: 'display', label: '数据展示' },
      { id: 'format', label: '数据格式' },
      { id: 'copy', label: '文案' },
      { id: 'buttons', label: '按钮' },
      { id: 'lists', label: '数据列表' },
    ],
  },
  {
    id: 'components',
    section: '组件库',
    items: [
      { id: 'c-button', label: 'Button' },
      { id: 'c-input', label: 'Input / Textarea' },
      { id: 'c-select', label: 'Select' },
      { id: 'c-switch', label: 'Switch / Segmented' },
      { id: 'c-chip', label: 'Chip / Tag / Badge' },
      { id: 'c-card', label: 'Card / Stat' },
      { id: 'c-table', label: 'Table' },
      { id: 'c-formrow', label: 'FormRow / Section' },
      { id: 'c-dialog', label: 'Dialog / Drawer' },
      { id: 'c-toast', label: 'Toast / Alert' },
      { id: 'c-multilang', label: '多语言字段' },
      { id: 'c-status', label: '状态机' },
    ],
  },
  {
    id: 'principles',
    section: '设计原则',
    items: [
      { id: 'p-density', label: '极致密度' },
      { id: 'p-monoscale', label: '数字一律 mono' },
      { id: 'p-i18n', label: 'i18n · 中英必填' },
      { id: 'p-status', label: '状态色三层' },
      { id: 'p-fallback', label: 'fallback 兜底链' },
    ],
  },
];

// ============================================================
// 1) Intro / Values / Cases — 介绍区，就近内联（原型同文件定义）
// ============================================================
function DSIntro() {
  return (
    <>
      <DSHeader
        kicker="v2026.05 · Design Language"
        title="Rivmoon Console Design System"
        sub="为 Rivmoon 矩阵运营后台沉淀的统一设计语言。覆盖 token、组件、模式与原则；目标是让每个新模块在没有设计师介入的情况下，开发也能按这套规范给出可上线的高保真页面。"
        badge={
          <span className="chip chip-accent" style={{ fontSize: 10 }}>
            v2026.05
          </span>
        }
      />
      <DSBlock title="为什么需要它">
        <p style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.7, margin: 0 }}>
          Rivmoon Console 服务多个 App 的多个团队 —
          运营、客服、财务、PM、技术。同一份数据可能要在五个不同的页面里出现，但表达的语义和粒度都不同。
          没有统一的设计语言，每个新页面都从零开始造轮子，最终会出现「同一个状态四种颜色、同一个按钮七种位置」的混乱。
          这套设计系统给出了所有底层约定：什么时候用 chip，什么时候用
          stat；什么颜色表示什么；多语言字段长什么样；状态机怎么走。
        </p>
      </DSBlock>
      <DSBlock title="使用方式">
        <ol
          style={{
            fontSize: 13,
            color: 'var(--fg-muted)',
            lineHeight: 1.8,
            paddingLeft: 18,
            margin: 0,
          }}
        >
          <li>开发新功能前先翻一遍这个文档，看是否已有现成组件 / 模式可用。</li>
          <li>遵守「全局样式」里的 token，不直接写颜色 / 字号 / 阴影字面量。</li>
          <li>
            「设计模式」给出业务级 UX 约定，比如「列表三件套（搜索 + 筛选 +
            分页）」、「多语言字段（中英 + 其他）」。
          </li>
          <li>「原则」是兜底：当你遇到没有现成模式的场景，按原则推导。</li>
        </ol>
      </DSBlock>
      <DSBlock title="维护责任">
        <div className="hstack" style={{ gap: 12, flexWrap: 'wrap' }}>
          {[
            { name: 'alice', role: 'DS 维护', scope: 'tokens + 模式' },
            { name: 'bohan', role: '代码 owner', scope: '组件实现' },
            { name: 'clara', role: '新组件评审', scope: 'PR 走 #ds-review' },
          ].map((p) => (
            <div
              key={p.name}
              className="card-flat"
              style={{ padding: '10px 12px', flex: '1 1 220px' }}
            >
              <div style={{ fontWeight: 500, fontSize: 12.5 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>{p.role}</div>
              <div style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 1 }}>
                {p.scope}
              </div>
            </div>
          ))}
        </div>
      </DSBlock>
    </>
  );
}

function DSValues() {
  const VALUES = [
    {
      k: '数据优先',
      v: '运营每天看的不是漂亮的图，是数字。所有页面以数据密度为第一目标，每屏看到的有效信息量越多越好。',
    },
    {
      k: '克制配色',
      v: '全站只有一个 accent（暖琥珀），所有强调走它；状态色（绿/黄/红/蓝）严格四级，不引入新色。',
    },
    {
      k: '可逆操作',
      v: '破坏性操作（下架、删除、扣 BONUS）必须走二次确认 + 操作日志；建设性操作（新增、保存）一键直达。',
    },
    {
      k: 'i18n 优先',
      v: '任何用户可见的文案都按多语言对待。中英必填 + 其他语言可后续维护 + fallback 英文。',
    },
    {
      k: '信息分层',
      v: 'fg-strong / fg / fg-muted / fg-faint 四级，配合字号建立 5–6 个视觉层级，不靠粗体堆叠。',
    },
    { k: '状态闭集', v: '每个业务对象都有一个有限的状态机；不允许「其他」选项。' },
  ];
  return (
    <>
      <DSHeader
        title="设计价值观"
        sub="六条不变的底层信念，所有 token、组件、模式都是它们的推论。"
      />
      <DSBlock title="核心价值">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 10,
          }}
        >
          {VALUES.map((v, i) => (
            <div key={v.k} className="card" style={{ padding: 14 }}>
              <div className="hstack" style={{ gap: 8, marginBottom: 6 }}>
                <span
                  className="mono"
                  style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600 }}
                >
                  0{i + 1}
                </span>
                <span style={{ fontWeight: 500, fontSize: 13 }}>{v.k}</span>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', lineHeight: 1.6 }}>{v.v}</div>
            </div>
          ))}
        </div>
      </DSBlock>
    </>
  );
}

function DSCases() {
  const CASES = [
    {
      name: 'Dashboard 数据总览',
      route: 'dashboard',
      note: '12 个 KPI 卡片 + 实时图表 + 漏斗 + 留存矩阵，体现「数据优先」与「信息分层」',
    },
    {
      name: 'App 管理 · 详情',
      route: 'apps',
      note: '标签页式编辑，体现「多语言集合（#413 起 en 为普通候选，非必含）」与「编辑态脏检测」',
    },
    {
      name: '书籍管理 · 上下架',
      route: 'books',
      note: '状态机驱动操作 + 多语言原因 + per-App 覆盖，是状态机模式的样板',
    },
    { name: '用户反馈', route: 'feedback', note: 'Inbox 布局 + 自动翻译 + 用户最近使用语言提示' },
    {
      name: '财务 · BONUS 发放',
      route: 'finance',
      note: '弹窗中嵌入用户最近使用语言提示 + 原因按该语言填写',
    },
    {
      name: '推送模板编辑',
      route: 'push',
      note: '标题 / 正文两段多语言字段并列，体现共享的 OtherLangsField 组件',
    },
  ];
  return (
    <>
      <DSHeader title="实践案例" sub="设计系统的所有规则都来自这些真实场景。点击可直接跳转。" />
      <DSBlock title="模块清单">
        <div className="vstack" style={{ gap: 8 }}>
          {CASES.map((c) => (
            <div
              key={c.name}
              className="card-flat"
              style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}
            >
              <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', width: 80 }}>
                #{c.route}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>{c.name}</div>
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>{c.note}</div>
              </div>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => window.dispatchEvent(new CustomEvent('nav', { detail: c.route }))}
              >
                <Icons.IconExternal size={11} />
                跳转
              </button>
            </div>
          ))}
        </div>
      </DSBlock>
    </>
  );
}

// ---------- Section dispatcher ----------
// 原型从 window.__dsTokenSections / __dsComponentSections / __dsPatternSections
// 查表；ESM 化后直接以 import 的组件建静态映射。
const SECTION_MAP: Record<string, ComponentType> = {
  intro: DSIntro,
  values: DSValues,
  cases: DSCases,
  colors: DSColors,
  layout: DSLayout,
  type: DSType,
  icons: DSIcons,
  theme: DSTheme,
  shadow: DSShadow,
  feedback: DSPatternFeedback,
  navigation: DSPatternNav,
  input: DSPatternInput,
  display: DSPatternDisplay,
  format: DSPatternFormat,
  copy: DSPatternCopy,
  buttons: DSPatternButtons,
  lists: DSPatternLists,
  'c-button': DSCButton,
  'c-input': DSCInput,
  'c-select': DSCSelect,
  'c-switch': DSCSwitch,
  'c-chip': DSCChip,
  'c-card': DSCCard,
  'c-table': DSCTable,
  'c-formrow': DSCFormRow,
  'c-dialog': DSCDialog,
  'c-toast': DSCToast,
  'c-multilang': DSCMultilang,
  'c-status': DSCStatus,
  'p-density': DSPDensity,
  'p-monoscale': DSPMono,
  'p-i18n': DSPI18n,
  'p-status': DSPStatus,
  'p-fallback': DSPFallback,
};

function DSContent({ active }: { active: string }) {
  const Comp = SECTION_MAP[active];
  return Comp ? (
    <Comp />
  ) : (
    <div style={{ color: 'var(--fg-faint)', padding: 40, textAlign: 'center' }}>该章节暂未实现</div>
  );
}

function DSPager({
  allItems,
  active,
  onNav,
}: {
  allItems: NavItem[];
  active: string;
  onNav: (id: string) => void;
}) {
  const idx = allItems.findIndex((i) => i.id === active);
  const prev = idx > 0 ? allItems[idx - 1] : null;
  const next = idx >= 0 && idx < allItems.length - 1 ? allItems[idx + 1] : null;
  return (
    <div
      className="hstack"
      style={{ marginTop: 28, paddingTop: 16, borderTop: '1px solid var(--border)' }}
    >
      {prev ? (
        <button type="button" className="btn btn-ghost" onClick={() => onNav(prev.id)}>
          <Icons.IconChevLeft size={12} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>上一节</div>
            <div style={{ fontSize: 12.5, fontWeight: 500 }}>{prev.label}</div>
          </div>
        </button>
      ) : (
        <div />
      )}
      <div style={{ flex: 1 }} />
      {next && (
        <button type="button" className="btn btn-ghost" onClick={() => onNav(next.id)}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>下一节</div>
            <div style={{ fontSize: 12.5, fontWeight: 500 }}>{next.label}</div>
          </div>
          <Icons.IconChevRight size={12} />
        </button>
      )}
    </div>
  );
}

/**
 * 设计系统活文档页面。
 * 两种挂载方式：① 独立路由 /design-system（embedded 缺省 = false，保留原型 -14 负 margin 抵消 page padding）；
 * ② 系统设置「设计系统」tab 内嵌（embedded = true，去掉负 margin，对齐原型 design-system.jsx 的 marginTop: embedded ? 0 : -14）。
 */
export default function DesignSystemPage({ embedded }: { embedded?: boolean } = {}) {
  const [active, setActive] = useState('intro');
  const allItems = NAV.flatMap((g) => g.items);
  return (
    <div style={{ display: 'flex', gap: 20, marginTop: embedded ? 0 : -14 }}>
      {/* Sub nav */}
      <aside
        style={{
          width: 200,
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          maxHeight: 'calc(100vh - 80px)',
          overflowY: 'auto',
          paddingRight: 6,
        }}
      >
        <div className="vstack" style={{ gap: 2 }}>
          {NAV.map((grp, gi) => (
            <div key={grp.id}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--fg-faint)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  padding: gi === 0 ? '0 10px 6px' : '12px 10px 6px',
                }}
              >
                {grp.section}
              </div>
              {grp.items.map((it) => (
                <button
                  type="button"
                  key={it.id}
                  onClick={() => setActive(it.id)}
                  className={`nav-item ${active === it.id ? 'active' : ''}`}
                  style={{
                    border: 0,
                    font: 'inherit',
                    textAlign: 'left',
                    width: '100%',
                    cursor: 'pointer',
                    background: active === it.id ? 'var(--bg-elev)' : 'transparent',
                    padding: '5px 10px 5px 14px',
                    fontSize: 12,
                  }}
                >
                  <span style={{ flex: 1 }}>{it.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </aside>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <DSContent active={active} />
        <DSPager allItems={allItems} active={active} onNav={setActive} />
      </div>
    </div>
  );
}
